import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'

// Hooks
import { useUniqueId } from 'src/hooks'

// Components
import { If, Unless, Text, Format } from 'src/Components'

// Constants
import { FIX_MORTGAGE_TYPE, ARM_MORTGAGE_TYPE } from 'src/constants/loan'
import ZeroBarChart from 'src/charts/zero-bar-chart'

// Styles
import styles from './RefiViz.module.scss'

const propTypes = {
  className: PropTypes.string,
  options: PropTypes.object,
  type: PropTypes.string,
  years: PropTypes.number,
  longterm: PropTypes.number,
  breakeven: PropTypes.number,
  closing: PropTypes.number,
  yearsInHome: PropTypes.number,
}

const RefiViz = ({
  className,
  options,
  type,
  years,
  longterm,
  breakeven,
  closing,
  yearsInHome,
}) => {
  const [chart, setChart] = useState(null)
  const chartId = useUniqueId('RefiViz_chart_')

  let variant
  const bad = longterm <= 0
  const caution = type === ARM_MORTGAGE_TYPE && yearsInHome > years
  if (bad) {
    variant = 'danger'
  } else if (caution) {
    variant = 'warning'
  } else {
    variant = 'success'
  }

  useEffect(() => {
    const chartSvg = document.getElementById(chartId)

    if (chartSvg && options) {
      const myChart = chart || new ZeroBarChart(chartSvg)
      myChart.update(
        Object.assign(options, { styles, xAxisClass: styles.xAxis })
      )
      setChart(myChart)
    }
  }, [options, chartId, chart])

  const vizYear =
    type === ARM_MORTGAGE_TYPE && yearsInHome > years ? years : yearsInHome

  return (
    <div className={classnames(className, styles.wrapper)}>
      <Text
        className={styles.overflow}
        size='s'
        weight='semibold'
        variant='neutralCool'
        variantCount='900'
      >
        <If condition={longterm >= 0}>
          <FormattedMessage
            id='RefiViz.chart.title.good'
            defaultMessage='Estimated savings over {years} years:'
            values={{
              years: vizYear,
            }}
          />
        </If>
        <If condition={longterm < 0}>
          <FormattedMessage
            id='RefiViz.chart.title.bad'
            defaultMessage='Estimated loss over {years} years:'
            values={{
              years: vizYear,
            }}
          />
        </If>
      </Text>
      <Text variant={variant} size='xl' weight='light'>
        <Format.Currency value={longterm} />
      </Text>
      <div className={`chart-container ${styles.chart}`}>
        <svg id={chartId} className='bar-chart' />
      </div>
      <div
        className={classnames(
          styles.legend,
          type !== ARM_MORTGAGE_TYPE && styles.hidden
        )}
      >
        <Text size='xs' element='span' variant='neutralWarm'>
          <FormattedMessage
            id='RefiViz.chart.legend'
            defaultMessage='Unknown savings or loss'
          />
        </Text>
      </div>
      <div className={styles.breakeven}>
        <Text size='xs' variant='neutralCool' variantCount='700'>
          <Unless condition={isNaN(breakeven)}>
            <FormattedMessage
              id='RefiViz.breakeven.good'
              defaultMessage='You might recoup closing costs by {year}, assuming average fees of {cost}.'
              values={{
                year: (
                  <Text.Strong>
                    {new Date().getFullYear() + Math.max(breakeven, 1)}
                  </Text.Strong>
                ),
                cost: <Format.Currency value={closing} />,
              }}
            />
          </Unless>
          <If condition={isNaN(breakeven) && type === ARM_MORTGAGE_TYPE}>
            <FormattedMessage
              id='RefiViz.breakeven.bad.arm'
              defaultMessage='It will probably take a while to recoup refi costs, and then your rate might change.'
            />
          </If>
          <If condition={isNaN(breakeven) && type === FIX_MORTGAGE_TYPE}>
            <FormattedMessage
              id='RefiViz.breakeven.bad.fix'
              defaultMessage="Looks like you're completely in the red - this option might not make sense."
            />
          </If>
        </Text>
      </div>
    </div>
  )
}

RefiViz.propTypes = propTypes

export default RefiViz
